import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";

import Page from "./Page";
import useData from "../../hooks/useData";
import Select from "../elements/Select";

const ClientPortalModules = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { executeAction, getFromServer } = useData();

  const [clientId, setClientId] = useState(location?.state?.clientId || null);
  const [clientName, setClientName] = useState(
    location?.state?.clientName || "",
  );
  const [clients, setClients] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const sortedModules = useMemo(
    () =>
      [...modules].sort((left, right) => {
        const leftOrder = Number(left?.sortOrder || 0);
        const rightOrder = Number(right?.sortOrder || 0);
        if (leftOrder === rightOrder) {
          return String(left?.name || "").localeCompare(
            String(right?.name || ""),
          );
        }
        return leftOrder - rightOrder;
      }),
    [modules],
  );

  const loadClients = async () => {
    const list = await getFromServer({
      entityName: "Client",
      filter: { isActive: true },
    });

    const normalized = Array.isArray(list) ? list : [];
    normalized.sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || "")),
    );
    setClients(normalized);
  };

  const loadConfig = async () => {
    if (!clientId) return;

    setIsLoading(true);
    try {
      const result = await executeAction({
        entityName: "Client",
        action: "getClientPortalModuleConfig",
        data: { clientId },
      });

      if (result?.isSuccessful) {
        setClientName(result?.data?.clientName || "");
        setModules(
          Array.isArray(result?.data?.modules) ? result.data.modules : [],
        );
        setSelectedKeys(
          Array.isArray(result?.data?.moduleKeys) ? result.data.moduleKeys : [],
        );
      } else {
        setModules([]);
        setSelectedKeys([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setClientId(location?.state?.clientId || null);
    setClientName(location?.state?.clientName || "");
  }, [location?.state?.clientId, location?.state?.clientName]);

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clientId) {
      setSelectedKeys([]);
      setModules([]);
      return;
    }

    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const toggleModule = (moduleKey) => {
    setSelectedKeys((current) => {
      const hasKey = current.includes(moduleKey);
      if (hasKey) return current.filter((key) => key !== moduleKey);
      return [...current, moduleKey];
    });
  };

  const handleToolbarClick = async (item) => {
    switch (item) {
      case "save": {
        if (!clientId) return;

        const saveResult = await executeAction({
          entityName: "Client",
          action: "saveClientPortalModuleConfig",
          data: {
            clientId,
            moduleKeys: selectedKeys,
          },
          displayMessage: true,
        });

        if (saveResult?.isSuccessful) {
          await loadConfig();
        }
        break;
      }

      case "refresh":
        await loadClients();
        await loadConfig();
        break;

      case "catalog":
        navigate("/client-portal-catalog", {
          state: {
            clientId,
            clientName,
          },
        });
        break;

      case "client":
        navigate("/client", { state: { clientId } });
        break;

      default:
        break;
    }
  };

  const selectedClientName = useMemo(() => {
    if (clientName) return clientName;
    const client = clients.find((item) => item.id === clientId);
    return client?.name || "Client";
  }, [clientName, clients, clientId]);

  return (
    <Page
      pageName="clientPortals"
      entityName={selectedClientName}
      includeToolbar={true}
      onToolItemClick={handleToolbarClick}
      items={[
        { name: "client", iconName: "Business" },
        { name: "catalog", iconName: "ViewModule" },
        { name: "save", iconName: "Save" },
        { name: "refresh", iconName: "Refresh" },
      ]}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography
            variant="h6"
            sx={{ mb: 2 }}
          >
            Client Module Assignment
          </Typography>

          <Box sx={{ maxWidth: 420, mb: 2 }}>
            <Select
              label="client"
              value={clientId || ""}
              items={clients}
              onChange={(value) => {
                setClientId(value || null);
                const client = clients.find((item) => item.id === value);
                setClientName(client?.name || "");
              }}
            />
          </Box>

          {!clientId ? (
            <Typography color="text.secondary">
              Select a client to assign modules.
            </Typography>
          ) : isLoading ? (
            <Typography color="text.secondary">Loading modules...</Typography>
          ) : sortedModules.length === 0 ? (
            <Typography color="text.secondary">
              No module catalog entries found in settings.
            </Typography>
          ) : (
            <List>
              {sortedModules.map((module) => {
                const key = module?.moduleKey;
                const checked = selectedKeys.includes(key);

                return (
                  <ListItem
                    key={key}
                    divider
                    secondaryAction={
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={() => toggleModule(key)}
                          />
                        }
                        label="Enabled"
                      />
                    }
                  >
                    <ListItemText
                      primary={`${module?.name || key} (${key})`}
                      secondary={`Global: ${module?.enabled === false ? "Disabled" : "Enabled"} | sortOrder: ${module?.sortOrder || 0}`}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>
      </Box>
    </Page>
  );
};

export default ClientPortalModules;
